'use client';
import { usePreferences } from '@/components/providers/preferences-provider';
import { formatDistance } from '@/utils/number-format';
import { convertDistance } from '@turf/turf';
import clsx from 'clsx';
import { useMemo } from 'react';

export interface DistanceProps {
  distance: number;
  completed?: number;
  originalUnit?: 'miles' | 'kilometers';
  className?: string;
}
export function Distance({
  distance,
  completed,
  className,
  originalUnit = 'miles',
}: DistanceProps) {
  const { unit } = usePreferences();

  const unitForTurf =
    unit === 'mile' ? 'miles' : unit === 'kilometer' ? 'kilometers' : 'miles';

  const distanceConverted = convertDistance(
    Math.abs(distance),
    originalUnit,
    unitForTurf,
  );
  const distanceFormatted = formatDistance(distanceConverted);
  const unitFormatted =
    unit === 'mile' ? 'mi' : unit === 'kilometer' ? 'km' : 'mi';

  const completedFormatted = useMemo(() => {
    if (completed === undefined) return undefined;
    const completedConverted = convertDistance(
      completed,
      originalUnit,
      unitForTurf,
    );
    return formatDistance(completedConverted);
  }, [completed, unitForTurf, originalUnit]);

  if (completedFormatted === undefined) {
    return (
      <div className={className}>
        {distanceFormatted}
        <span className="ml-1">{unitFormatted}</span>
      </div>
    );
  }
  return (
    <div className={clsx('inline-flex gap-1', className)}>
      <span>{completedFormatted}</span>
      <span>/</span>
      <span>{distanceFormatted}</span>
      <span>{unitFormatted}</span>
    </div>
  );
}
